import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * UploadLightbox Component
 *
 * Unified lightbox for viewing uploads with:
 * - Pinch-to-zoom (mobile) and scroll-wheel zoom (desktop)
 * - Tag management
 * - Uploader information
 * - File metadata display
 *
 * Used by both the crosshair gallery and admin uploads page.
 */
import { useState, useCallback } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { Badge } from '../../ui/badge';
import { TagInput } from './TagInput';
import { formatFileSize, formatDualDates } from '../../../utils/uploads';
import { resetVisualViewport } from '../../../utils/viewport';
const LightboxCaption = ({ upload, onAddTag, onRemoveTag, showTags = true, showUploader = true, showMetadata = true, showEntity = false, }) => {
    return (_jsxs("div", { className: "absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm p-4 z-10", onClick: (e) => e.stopPropagation(), children: [showEntity && upload.entity && (_jsxs("div", { className: "text-center mb-2", children: [_jsx("span", { className: "text-white/60 text-xs uppercase", children: upload.entity.type }), _jsx("p", { className: "text-white font-medium", children: upload.entity.label })] })), _jsx("p", { className: "text-white text-sm font-medium mb-2 text-center", children: upload.filename }), showMetadata && (() => {
                const dates = formatDualDates(upload.filedate, upload.uploadTime);
                return (_jsxs("div", { className: "flex flex-col items-center gap-1 mb-2 text-xs", children: [_jsxs("div", { className: "text-white font-medium", children: ["File: ", dates.primary] }), _jsxs("div", { className: "flex flex-wrap items-center justify-center gap-3 text-white/70", children: [dates.hasBothDates && (_jsxs("span", { children: ["Uploaded: ", dates.secondary] })), upload.size && (_jsx("span", { children: formatFileSize(upload.size) })), upload.mimetype && (_jsx("span", { className: "uppercase", children: upload.mimetype.split('/')[1] }))] })] }));
            })(), showUploader && upload.uploader && (_jsxs("div", { className: "flex items-center justify-center gap-2 mb-2", children: [_jsxs("span", { className: "text-xs text-white/70", children: ["Uploaded by ", _jsx("span", { className: "text-white font-medium", children: upload.uploader.name })] }), upload.uploader.deleted && (_jsx(Badge, { variant: "destructive", className: "text-[10px] px-1.5 py-0", children: "Former staff" }))] })), showTags && onAddTag && onRemoveTag && (_jsx(TagInput, { tags: upload.UploadTags || [], onAddTag: onAddTag, onRemoveTag: onRemoveTag, variant: "overlay" }))] }));
};
export const UploadLightbox = ({ uploads, currentIndex, onClose, onIndexChange, onAddTag, onRemoveTag, onConfirmRemoveTag, showTags = true, showUploader = true, showMetadata = true, showEntity = false, }) => {
    const [index, setIndex] = useState(currentIndex);
    const currentUpload = uploads[index];
    // Wrap onClose to reset visual viewport after lightbox closes
    // This prevents the nav bar from appearing off-screen on mobile after pinch-zoom
    const handleClose = useCallback(() => {
        // Small delay to ensure lightbox has unmounted before resetting viewport
        setTimeout(() => {
            resetVisualViewport();
        }, 50);
        onClose();
    }, [onClose]);
    // Create slides array for lightbox
    const slides = uploads.map((upload) => ({
        src: upload.url,
        alt: upload.filename,
    }));
    const handleIndexChange = useCallback((newIndex) => {
        setIndex(newIndex);
        onIndexChange?.(newIndex);
    }, [onIndexChange]);
    const handleAddTag = useCallback(async (tag) => {
        if (onAddTag && currentUpload) {
            await onAddTag(currentUpload.id, tag);
        }
    }, [onAddTag, currentUpload]);
    const handleRemoveTag = useCallback(async (tag) => {
        if (onConfirmRemoveTag) {
            const confirmed = await onConfirmRemoveTag(tag);
            if (!confirmed)
                return;
        }
        if (onRemoveTag && currentUpload) {
            await onRemoveTag(currentUpload.id, tag);
        }
    }, [onRemoveTag, onConfirmRemoveTag, currentUpload]);
    if (!currentUpload) {
        return null;
    }
    return (_jsx(Lightbox, { open: true, close: handleClose, index: index, on: {
            view: ({ index: newIndex }) => handleIndexChange(newIndex),
        }, slides: slides, plugins: [Zoom], zoom: {
            scrollToZoom: true,
            maxZoomPixelRatio: 4,
            wheelZoomDistanceFactor: 100,
        }, carousel: {
            finite: slides.length <= 1,
        }, styles: {
            container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
        }, render: {
            buttonPrev: slides.length <= 1 ? () => null : undefined,
            buttonNext: slides.length <= 1 ? () => null : undefined,
            slideFooter: () => (_jsx(LightboxCaption, { upload: currentUpload, onAddTag: onAddTag ? handleAddTag : undefined, onRemoveTag: onRemoveTag ? handleRemoveTag : undefined, showTags: showTags, showUploader: showUploader, showMetadata: showMetadata, showEntity: showEntity })),
        } }));
};
export default UploadLightbox;
